import {
    Injectable, inject
} from "@angular/core";
import {
    ModelAction, ISearchSort
} from "@shopliftr/common-js/data";
import {
    BulkResponse, BulkUtil, CreateRequest, DeleteRequest, DeleteResponse, GetResponse,
    RequestUtil, SearchResponse, SubmitRequest, SubmitResponse, User,
    UserSearchFilterOptions, UserSetPasswordSubmission, UpdateRequest
} from "@shopliftr/common-js/shared";
import {
    IFullAccessInternalApiClientService
} from "../../../../interfaces/internal-api-client";
import { InternalApiRequestService } from "../../request/internal-api/internal-api-request.service";
import { ListUtil } from "../../../../list/util/list-util";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

/**
 * API client service for managing user CRUD operations through internal API endpoints.
 *
 * Implements the API Client Service pattern providing full CRUD access for the User
 * model via the Internal API v2 `/v2/user` routes. This service encapsulates all
 * user endpoint paths, builds typed requests using RequestUtil, and maps responses
 * to User domain models.
 *
 * In addition to the standard 11 IFullAccessInternalApiClientService methods, this
 * service also exposes `setPassword$` which calls `POST /v2/user/set-password`
 * via `InternalApiRequestService.submit$` using a `UserSetPasswordSubmission` payload.
 *
 * **Service Pattern:** API Client Service
 *
 * **Responsibilities:**
 * - Encapsulates user API endpoint paths
 * - Builds typed requests using RequestUtil
 * - Maps responses to User domain models
 * - Provides full CRUD operations: create, bulkCreate, get, bulkGet, getAll,
 *   search, update, bulkUpdate, updateWithChanges, delete, bulkDelete
 * - Provides custom operation: setPassword
 *
 * **Provision:** Singleton service provided in 'root'
 *
 * @pattern IFullAccessInternalApiClientService
 * @providedIn root
 * @implements {IFullAccessInternalApiClientService<User>} Full CRUD access for User model
 *
 * @example
 * ```typescript
 * // Inject the service
 * private readonly _userApiClientService = inject(UserApiClientService);
 *
 * // Get a user
 * this._userApiClientService.get$('user-id').subscribe(user => {
 *   console.log(user.email);
 * });
 *
 * // Search users
 * const filterOptions = new UserSearchFilterOptions();
 * filterOptions.teamId = 'team-id';
 * this._userApiClientService.search$(filterOptions, 0, 10).subscribe(response => {
 *   console.log(response.results);
 * });
 *
 * // Set password via v2 submit endpoint
 * const submission = new UserSetPasswordSubmission();
 * submission.password = 'newP@ssw0rd';
 * submission.currentPassword = 'oldP@ssw0rd';
 * this._userApiClientService.setPassword$(submission).subscribe((response) => {
 *   console.log('Password updated', response.payload);
 * });
 * ```
 *
 * @see {@link IFullAccessInternalApiClientService} for the full-access API client interface
 * @see {@link UserSetPasswordSubmission} for the set-password payload model
 */
@Injectable({ providedIn: "root" })
export class UserApiClientService implements IFullAccessInternalApiClientService<User> {

    /**
     * Base API path for user endpoints.
     * Used as the root path segment for constructing API URLs.
     *
     * @readonly
     */
    readonly basePath = "user";

    /**
     * Internal API request service for making HTTP calls to the backend.
     * Injected using Angular's inject() function.
     *
     * @private
     * @readonly
     */
    private readonly _internalApiRequestService = inject(InternalApiRequestService);

    /**
     * Retrieves multiple users by their unique identifiers in a single bulk request.
     *
     * @param ids - Array of unique identifiers of the users to retrieve
     * @returns Observable that emits a BulkResponse containing the requested users
     */
    bulkGet$(ids: Array<string>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(ids.map((id: string) => {
            const request = RequestUtil.createGetRequest<User>(id, User.modelName, ModelAction.ClientGet);
            return BulkUtil.createRequestItem(request);
        }));
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Retrieves a single user by its unique identifier.
     *
     * @param id - The unique identifier of the user to retrieve
     * @returns Observable that emits the requested User entity
     */
    get$(id: string): Observable<User> {
        const request = RequestUtil.createGetRequest<User>(id, User.modelName, ModelAction.ClientGet);
        return this._internalApiRequestService.get$(request, this.basePath).pipe(
            map((response: GetResponse<User>) => response.payload)
        );
    }

    /**
     * Retrieves all users matching the specified filter criteria without pagination.
     *
     * @param filterOptions - Filter criteria to apply to the search
     * @param sort - Optional sorting configuration
     * @returns Observable that emits an array of all matching User entities
     */
    getAll$(filterOptions: UserSearchFilterOptions, sort?: Array<ISearchSort>): Observable<Array<User>> {
        const request = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch, User.modelName, undefined, undefined, filterOptions, sort
        );
        return this._internalApiRequestService.getAll$<User, UserSearchFilterOptions>(request, this.basePath);
    }

    /**
     * Performs a paginated search for users matching the specified criteria.
     *
     * @param filterOptions - Filter criteria to apply to the search
     * @param page - Zero-based page number for pagination (optional)
     * @param pageSize - Number of results per page (optional)
     * @param sort - Optional sorting configuration
     * @returns Observable that emits a SearchResponse containing matching users and pagination metadata
     */
    search$(
        filterOptions: UserSearchFilterOptions,
        page?: number,
        pageSize?: number,
        sort?: Array<ISearchSort>
    ): Observable<SearchResponse<User>> {
        const request = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            User.modelName,
            ListUtil.getFromValue(page, pageSize),
            pageSize,
            filterOptions,
            sort
        );
        return this._internalApiRequestService.search$<User, UserSearchFilterOptions>(request, this.basePath);
    }

    /**
     * Creates a new user in the system.
     *
     * @param fields - The user data to create
     * @returns Observable that emits the created user with assigned ID and metadata
     */
    create$(fields: User): Observable<User> {
        const createRequest = RequestUtil.createCreateRequest<User, void>(
            ModelAction.ClientCreate, fields.cloneForAction(ModelAction.ClientCreate)
        );
        return this._internalApiRequestService.create$(createRequest, this.basePath).pipe(
            map((response) => response.payload)
        );
    }

    /**
     * Creates multiple users in a single bulk request.
     *
     * @param items - Array of create requests for users to create
     * @returns Observable that emits a BulkResponse with the results of each create operation
     */
    bulkCreate$(items: Array<CreateRequest<User, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            items.map((item) => BulkUtil.createRequestItem(item))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Updates an existing user with new field values or removes specified fields.
     *
     * @param id - The unique identifier of the user to update
     * @param version - The current version of the user for optimistic locking
     * @param fields - The user fields to update with new values
     * @param removeFields - Array of field names to remove from the user (optional)
     * @returns Observable that emits the updated user
     */
    update$(id: string, version: number, fields: User, removeFields?: Array<string>): Observable<User> {
        const updateFields = fields.cloneForAction(ModelAction.ClientUpdate);
        const updateRequest: UpdateRequest<User> = RequestUtil.createUpdateRequest(
            id, ModelAction.ClientUpdate, updateFields, version, removeFields
        );
        return this._internalApiRequestService.update$(updateRequest, this.basePath).pipe(
            map((response) => response.payload)
        );
    }

    /**
     * Updates the record of the original user with the updated model provided.
     *
     * @param original - The original user model
     * @param updated - The updated user model with changes applied
     * @returns Observable that emits the updated user
     */
    updateWithChanges$(original: User, updated: User): Observable<User> {
        return this._internalApiRequestService.updateWithChanges$(original, updated, this.basePath);
    }

    /**
     * Updates multiple users in a single bulk request.
     *
     * @param requests - Array of update requests for users to update
     * @returns Observable that emits a BulkResponse with the results of each update operation
     */
    bulkUpdate$(requests: Array<UpdateRequest<unknown, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            requests.map((request) => BulkUtil.createRequestItem(request))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Deletes a user by its unique identifier and version.
     *
     * @param id - The unique identifier of the user to delete
     * @param version - The current version of the user for optimistic locking
     * @returns Observable that emits a DeleteResponse on successful deletion
     */
    delete$(id: string, version: number): Observable<DeleteResponse> {
        const deleteRequest = RequestUtil.createDeleteRequest<User>(
            id, User.modelName, ModelAction.ClientDelete, version
        );
        return this._internalApiRequestService.delete$(deleteRequest, this.basePath);
    }

    /**
     * Deletes multiple users in a single bulk request.
     *
     * @param items - Array of delete requests for users to delete
     * @returns Observable that emits a BulkResponse with the results of each delete operation
     */
    bulkDelete$(items: Array<DeleteRequest<unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            items.map((item) => BulkUtil.createRequestItem(item))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Sets the password for a user via the `POST /v2/user/set-password` endpoint.
     *
     * Uses `UserSetPasswordSubmission` as the request payload which carries both the
     * new `password` and, when changing on behalf of the currently authenticated user,
     * the `currentPassword` for verification.
     *
     * The operation is routed through `InternalApiRequestService.submit$` with a path of
     * `user/set-password` to hit the correct v2 route.
     *
     * `UserSetPasswordSubmission.password` and `.currentPassword` are marked
     * `@redactedValueField()` in `@shopliftr/common-js`, so a plain `request.serialize()`
     * would silently strip both values from the outbound payload (`{}`), sending the
     * server no credential to act on. This is the one legitimate case where a client
     * request must transmit a secret it will never persist itself, so this method
     * deliberately opts in via `submit$`'s `serializeOptions` parameter with
     * `{ includeRedactedFields: true }`, scoped to this single call site only — `submit$`
     * itself still defaults to fully redacted serialization for every other caller.
     * Do not remove or "simplify" this option away; doing so reintroduces the credential
     * drop.
     *
     * @param submission - The set-password payload containing the new (and optionally current) password
     * @returns Observable that emits a `SubmitResponse<User>` on success
     *
     * @example
     * ```typescript
     * const submission = new UserSetPasswordSubmission();
     * submission.password = 'newP@ssw0rd';
     * submission.currentPassword = 'oldP@ssw0rd';
     * this._userApiClientService.setPassword$(submission).subscribe((response) => {
     *   console.log('Password updated successfully', response.payload);
     * });
     * ```
     */
    setPassword$(submission: UserSetPasswordSubmission): Observable<SubmitResponse<User>> {
        const request: SubmitRequest<UserSetPasswordSubmission> = RequestUtil.createSubmitRequest(submission);
        return this._internalApiRequestService.submit$<UserSetPasswordSubmission, User>(
            request, `${this.basePath}/set-password`, { includeRedactedFields: true }
        );
    }
}
